/**
 * Supported phone country codes for login.
 *
 * The `subscriber` regex matches what the user types AFTER the dial code, with
 * no spaces or dashes. Keep these in sync with the backend: it validates via
 * validator.isMobilePhone(phoneNumber) with no locale, which tries every locale
 * and accepts the first match. The patterns below are deliberately narrower than
 * validator's, so anything that passes here also passes there.
 *
 * validator@11.1.0 reference patterns:
 *   bg-BG: /^(\+?359|0)?8[789]\d{7}$/
 *   de-DE: /^(\+49)?0?1(5[0-25-9]\d|6([23]|0\d?)|7([0-57-9]|6\d))\d{7}$/
 *
 * Note that validator's de-DE pattern rejects spaces and dashes, hence
 * normalizePhone() strips them before submit.
 */
export const PHONE_COUNTRIES = [
  {
    code: "BG",
    dialCode: "+359",
    label: "България",
    flag: "🇧🇬",
    placeholder: "888123456",
    // Bulgarian mobiles: 87/88/89 + 7 digits.
    subscriber: /^8[789]\d{7}$/,
  },
  {
    code: "DE",
    dialCode: "+49",
    label: "Германия",
    flag: "🇩🇪",
    placeholder: "15112345678",
    // German mobiles: 15x/16x/17x prefixes, length varies by range.
    subscriber: /^1(5[0-25-9]\d|6([23]|0\d?)|7([0-57-9]|6\d))\d{7}$/,
  },
];

export const DEFAULT_DIAL_CODE = "+359";

export const getCountryByDialCode = (dialCode) =>
  PHONE_COUNTRIES.find((c) => c.dialCode === dialCode) || PHONE_COUNTRIES[0];

/**
 * Strip everything the backend's regex would choke on: spaces, dashes,
 * parentheses, a leading national zero, and a pasted dial code.
 */
export const normalizePhone = (value, dialCode) => {
  let digits = (value || "").replace(/[\s\-().]/g, "");

  const bare = dialCode.replace("+", "");
  if (digits.startsWith(dialCode)) digits = digits.slice(dialCode.length);
  else if (digits.startsWith(`00${bare}`)) digits = digits.slice(bare.length + 2);
  else if (digits.startsWith(bare)) digits = digits.slice(bare.length);

  return digits.replace(/^0+/, "");
};

/**
 * Validate a full number (dial code included) against the supported countries,
 * for forms that take the whole thing as free text rather than splitting the
 * dial code into its own field.
 *
 * Login matches users by exact string, so a record saved in any other shape can
 * never be logged into — this is what keeps the two in sync.
 */
export const isSupportedPhone = (value) => {
  const digits = (value || "").replace(/[\s\-().]/g, "");
  return PHONE_COUNTRIES.some(
    (country) =>
      digits.startsWith(country.dialCode) &&
      country.subscriber.test(digits.slice(country.dialCode.length))
  );
};

/**
 * Collapse a full number to the exact E.164 form login will look up.
 */
export const toE164 = (value) => {
  const digits = (value || "").replace(/[\s\-().]/g, "");
  const country = PHONE_COUNTRIES.find((c) => digits.startsWith(c.dialCode));
  if (!country) return digits;
  return `${country.dialCode}${normalizePhone(digits, country.dialCode)}`;
};

/**
 * Yup test shared by both login screens. Validates the subscriber part against
 * whichever country is currently selected.
 */
export const phoneNumberTest = {
  name: "phone-for-country",
  message: "Невалиден телефонен номер",
  test(value) {
    if (!value) return true; // let .required() own the empty case
    const country = getCountryByDialCode(this.parent?.countryCode);
    return country.subscriber.test(normalizePhone(value, country.dialCode));
  },
};
